import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import type { BaseModalProps } from '@trinity/chat-components/hooks';
import { useModal } from '@trinity/chat-components/hooks';
import type { Chat } from '@trinity/chat';
import { useRenameChatInstanceMutation } from './hooks';
import { SecondaryButton } from '../presentation/Button.Secondary';
import { PrimaryButton } from '../presentation/Button.Primary';

type Props = {
  chatInstance: Pick<Chat.Instance, 'id' | 'name'>;
} & BaseModalProps;

export const RenameChatModal = ({ chatInstance, open, onClose }: Props) => {

  const [value, setValue] = useState(chatInstance.name || '');

  const mutation = useRenameChatInstanceMutation({ chatInstanceId: chatInstance.id, onSuccess: onClose });

  const handleConfirmation = useCallback(() => {
    return mutation.mutateAsync(value);
  }, [mutation, value]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  const canSubmit = !!value && !mutation.isPending;

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canSubmit) {
      e.preventDefault();
      handleConfirmation();
    }
  }, [canSubmit, handleConfirmation]);

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      onClose={onClose}
      open={open}>

      <Title>Rename Chat</Title>

      <DialogContent>
        <Box
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'var(--font-reg)',
              fontSize: 16,
              lineHeight: 32,
              letterSpacing: -.25,
            },
          }}>
          <TextField
            autoFocus
            fullWidth
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            value={value}
            variant="standard" />
        </Box>
      </DialogContent>

      <Footer>
        <SecondaryButton
          onClick={handleCancel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          disabled={!canSubmit}
          onClick={handleConfirmation}>
          Save
        </PrimaryButton>
      </Footer>

    </Dialog>
  );
};

const Title = styled(DialogTitle)(({ theme }) => ({
  marginBottom: 10,
  fontFamily: theme.fonts.bold,
  fontSize: 22,
  textAlign: 'center',
  color: theme.palette.text.primary,
}));

const Footer = styled(DialogActions)(({
  display: 'flex',
  justifyContent: 'center',
}));

export const useRenameChatModal = () => useModal(RenameChatModal);